import React from 'react';
import PropTypes from 'prop-types';
import { graphql, Link } from 'gatsby';
import parse from 'html-react-parser';
import styles from '@components/utils/styles';
import styled from 'styled-components';
import Head from '@repo/ui-components/Head';
import createOpenGraphTagsForSupportSite from '@repo/utils/createOpenGraphTagsForSupportSite';

const ProductSupportContainer = styled.div`
  margin: 1em;
  padding: 1em;
`;

const ProductSupportBody = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const ProductSupportColOne = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 300px;
`;

const ProductSupportColTwo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const RelatedArticlesList = styled.ul`
  list-style: disc;
  padding-inline-start: 20px;
`;

const ProductSupportImage = styled.img`
  align-self: start;
  width: 180px;
  height: 180px;
`;

const ProductSupportTemplate = ({ data }) => {
  const { title, relatedProducts, relatedModels } = data?.page || {};
  const product = relatedProducts[0] ?? {};
  const articles = data?.articles?.nodes ?? [];

  return (
    <>
      <Head
        title={data?.page?.SEOMetadata?.title}
        description={data?.page?.SEOMetadata?.description}
        linkTags={[
          <link
            key="canonical"
            rel="canonical"
            href={data?.page?.SEOMetadata?.canonicalUrl}
          />,
        ]}
        noIndex={data?.page?.SEOMetadata?.noIndex}
        noFollow={data?.page?.SEOMetadata?.noFollow}
        meta={createOpenGraphTagsForSupportSite({
          title: data?.page?.SEOMetadata?.ogTitle,
          url: data?.page?.SEOMetadata?.ogUrl,
          image: data?.page?.SEOMetadata?.ogImage,
          description: data?.page?.SEOMetadata?.ogDescription,
        })}
      />
      <ProductSupportContainer>
        <h3>{title}</h3>
        <ProductSupportBody>
          <ProductSupportColOne>
            <ProductSupportImage
              src={product?.store?.previewImageUrl}
              alt={product?.title}
            />
          </ProductSupportColOne>
          <ProductSupportColTwo>
            {product?.store?.descriptionHtml
              ? parse(product?.store?.descriptionHtml)
              : ''}
            <h5>Model Numbers</h5>
            <p>{relatedModels?.join(' | ')}</p>
            <h5>Downloads</h5>
            {product?.manuals?.map(manual => {
              return (
                <span key={manual.asset.id}>
                  <i
                    className="bi bi-file-earmark-pdf"
                    style={{ fontSize: '2rem', color: styles.colors.blue[500] }}
                  />
                  <a href={manual.asset.url}>{manual.asset.originalFilename}</a>
                </span>
              );
            })}
          </ProductSupportColTwo>
        </ProductSupportBody>
        <hr />
        <h5>Articles About This Product</h5>
        <div>
          <RelatedArticlesList className="related-articles">
            {articles &&
              articles.map(({ slug, title, _key }) => {
                return (
                  <li key={_key}>
                    <Link to={`/articles/${slug?.current}`}>{title}</Link>
                  </li>
                );
              })}
          </RelatedArticlesList>
        </div>
      </ProductSupportContainer>
    </>
  );
};

export const query = graphql`
  query ProductSupportPage($pageId: String = "") {
    page: sanityProductSupportPage(id: { eq: $pageId }) {
      SEOMetadata {
        canonicalUrl
        description
        noFollow
        noIndex
        title
        ogDescription
        ogTitle
        ogUrl
        ogImage {
          asset {
            id
            url
          }
        }
      }
      id
      relatedProducts {
        id
        manuals {
          asset {
            id
            mimeType
            url
            title
            description
            originalFilename
          }
        }
        productSkus
        store {
          previewImageUrl
          title
          descriptionHtml
        }
      }
      relatedModels
      title
    }
    articles: allSanitySupportArticle(
      filter: { relatedSupportPages: { elemMatch: { id: { eq: $pageId } } } }
    ) {
      nodes {
        slug {
          current
        }
        title
        summary
      }
    }
  }
`;

ProductSupportTemplate.propTypes = {
  data: PropTypes.shape({
    page: PropTypes.shape({
      SEOMetadata: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        canonicalUrl: PropTypes.string,
        noIndex: PropTypes.bool,
        noFollow: PropTypes.bool,
        ogTitle: PropTypes.string,
        ogUrl: PropTypes.string,
        ogDescription: PropTypes.string,
        ogImage: PropTypes.shape({}),
      }),
      relatedProducts: PropTypes.array,
    }),
    articles: PropTypes.shape({
      nodes: PropTypes.shape({
        slug: PropTypes.shape({
          current: PropTypes.string,
        }),
        title: PropTypes.string,
        summary: PropTypes.string,
      }),
    }),
  }),
};

ProductSupportTemplate.defaultProps = {
  data: {
    page: {
      relatedProducts: [],
    },
  },
};

export default ProductSupportTemplate;
